import { ISortByQuery } from '@common/api.models';
import { DEFAULT_TABLE_PAGE_SIZE_OPTIONS } from '@common/constants';

import { SearchFilter } from '@common/searchFilter/SearchFilter';
import { IFilterFieldConfig, SearchFilterType } from '@common/searchFilter/searchFilter.models';
import { SearchPageBase } from '@common/SearchPageBase';
import {
  createTableColumn,
  createTableDateTimeColumn,
  createTableLinkWithOpenNewTabColumn,
  createTableUserRoleColumn,
} from '@common/tableColumn.services';
import {
  Column,
  ComponentType,
  createTableCell,
  Icon,
  IResponsivePaginatedTableProps,
  isNullOrWhiteSpace,
  ResponsivePaginatedTable,
  TableCellExtras,
  useMediaQuery,
} from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import {
  useFetchSrpOptionsWithSearch,
  useFetchTwcmOptionsWithSearch,
  useFetchTwcOptionsWithSearch,
} from '@userProfile/userProfile.queries';
import * as React from 'react';
import {
  useFetchCheckerCompanyOptions,
  useFetchDesignerCompanyOptions,
} from '@company/projectCompany/projectCompany.queries';
import { useFetchAllRiskCategories } from '@riskCategory/riskCategory.queries';
import { useFetchAllVoItems } from '@voItem/voItem.queries';
import {
  IMasterSchedule,
  IMasterScheduleSearchFilter,
  IMasterScheduleSearchResult,
  EMasterScheduleSearchKey,
  IMasterScheduleHeader,
} from './masterSchedule.models';
// eslint-disable-next-line import/order
import { useFetchMasterSchedule } from './masterSchedule.queries';
import '@gcl/react-core/dist/themes.scss';
import { useNavigate } from '@routes/routes.hooks';
import { useAppConfigs, useProjectId } from '@config/config.hooks';
import { StringParam, NumberParam, ArrayParam, BooleanParam } from 'use-query-params';
import {
  getDateTimeFilterFieldNameFrom,
  getDateTimeFilterFieldNameTo,
  getSelectFilterFieldNameId,
} from '@common/searchFilter/searchFilter.services';
import { useSearchFilterUrlParam } from '@common/searchFilter/searchFilterUrlParam.hooks';
import { DownloadButton } from '@file/DownloadButton';
import { useUserProfilePreferencesPageSize } from '@userProfile/userProfilePreferences.hooks';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import {
  exportProjectMasterScheduleQrPdfAllAsync,
  exportProjectMasterScheduleSearchExcelAsync,
  exportProjectMasterScheduleSearchPdfAsync,
} from './masterSchedule.services';
import './MasterScheduleList.scss';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { ECompanyTypeIdConst } from '@company/companyType/companyType.constants';
import { hasPermissionInArray } from '@userProfile/permission/permission.services';
import { EMasterSchedulePermission } from '@userProfile/permission/permission.constants';
import { IButtonNavBarItem } from '@app/ButtonNavBar';
import '@common/Common.scss';

export interface IMasterScheduleListProps {}

export const MasterScheduleList: React.FunctionComponent<IMasterScheduleListProps> = () => {
  const appConfigs = useAppConfigs();
  const isXs = useMediaQuery('(max-width: calc(640px))');

  const DEFAULT_SORT_BY_FIELD_NAME = 'refNo';
  const projectId = useProjectId();

  // #region Search Field Configs
  const MasterScheduleSearchFieldConfigs = React.useMemo(() => {
    return [
      {
        fieldName: EMasterScheduleSearchKey.REF_NO,
        displayName: 'T1',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.DESIGN_PACKAGE,
        displayName: 'Package',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.RISK_CATEGORY,
        displayName: 'Risk Category',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchAllRiskCategories({ shouldConvertOptions: true }),
        useFetchOptionsProps: {},
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.VO_ITEM,
        displayName: 'VO Item',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchAllVoItems({ shouldConvertOptions: true }),
        useFetchOptionsProps: {},
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.IS_CANCELLED,
        displayName: 'Is Cancelled',
        type: SearchFilterType.Boolean,
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.DESIGNER,
        displayName: 'Designer',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchDesignerCompanyOptions({}),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.CHECKER,
        displayName: 'Checker',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchCheckerCompanyOptions({}),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.TWC,
        displayName: 'TWC/GE',
        allowMultipleSelect: true,
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchTwcOptionsWithSearch({ shouldConvertOptions: true }),
        selectGroupOptionByKey: 'data.team.teamName',
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.TWCM,
        displayName: 'TWCM',
        allowMultipleSelect: true,
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchTwcmOptionsWithSearch({ shouldConvertOptions: true }),
        selectGroupOptionByKey: 'data.team.teamName',
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.SRP,
        displayName: 'SRP/QP',
        allowMultipleSelect: true,
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchSrpOptionsWithSearch({ shouldConvertOptions: true }),
        selectGroupOptionByKey: 'data.team.teamName',
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.START_CONSTRUCTION_DATE,
        displayName: 'Start Construction',
        type: SearchFilterType.DateTime,
      } as IFilterFieldConfig,
      {
        fieldName: EMasterScheduleSearchKey.PROCUREMENT_FABRICATION_DATE,
        displayName: 'Procurement/Fabrication',
        type: SearchFilterType.DateTime,
      } as IFilterFieldConfig,
    ];
  }, [projectId]);
  // #endregion Search Field Configs

  const { localFilter, setLocalFilter } = useSearchFilterUrlParam(
    {
      [EMasterScheduleSearchKey.REF_NO]: StringParam,
      [EMasterScheduleSearchKey.DESIGN_PACKAGE]: StringParam,
      [`${getDateTimeFilterFieldNameFrom(EMasterScheduleSearchKey.START_CONSTRUCTION_DATE)}`]: StringParam,
      [`${getDateTimeFilterFieldNameTo(EMasterScheduleSearchKey.START_CONSTRUCTION_DATE)}`]: StringParam,
      [`${getDateTimeFilterFieldNameFrom(EMasterScheduleSearchKey.PROCUREMENT_FABRICATION_DATE)}`]: StringParam,
      [`${getDateTimeFilterFieldNameTo(EMasterScheduleSearchKey.PROCUREMENT_FABRICATION_DATE)}`]: StringParam,
      [`${getSelectFilterFieldNameId(EMasterScheduleSearchKey.RISK_CATEGORY)}`]: NumberParam,
      [`${getSelectFilterFieldNameId(EMasterScheduleSearchKey.VO_ITEM)}`]: NumberParam,
      [`${getSelectFilterFieldNameId(EMasterScheduleSearchKey.DESIGNER)}`]: NumberParam,
      [`${getSelectFilterFieldNameId(EMasterScheduleSearchKey.CHECKER)}`]: NumberParam,
      [EMasterScheduleSearchKey.TWC]: ArrayParam,
      [EMasterScheduleSearchKey.SRP]: ArrayParam,
      [EMasterScheduleSearchKey.IS_CANCELLED]: BooleanParam,
    },
    MasterScheduleSearchFieldConfigs,
    {
      [EMasterScheduleSearchKey.IS_CANCELLED]: false,
    },
  );

  const masterScheduleFilterObj = React.useMemo(() => {
    const temp = {} as any;

    Object.keys(localFilter).forEach((key) => {
      if (!key.includes('.')) temp[key] = localFilter[key];
      else {
        const splitedKey = key.split('.');
        const child = localFilter[splitedKey[0]] ?? {};
        child[splitedKey[1]] = localFilter[key];
        temp[splitedKey[0]] = child;
      }
    });

    return temp as IMasterScheduleSearchFilter;
  }, [JSON.stringify(localFilter)]);

  const { initPageSize, pageSize, setPageSize } = useUserProfilePreferencesPageSize(
    EUserProfilePreferenceAttributeName.T1_LIST_PAGE_SIZE,
  );

  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [sortByQuery, setSortByQuery] = React.useState<ISortByQuery | undefined>({
    sortByField: DEFAULT_SORT_BY_FIELD_NAME,
  });
  const { navigate, generateRoutePath, projectNoFromAppConfigs } = useNavigate();

  const {
    data: masterSchedulePagedResult,
    isLoading: isLoadingMasterScheduleSearch,
    isRefetching: isRefetchingMasterScheduleSearch,
    error: errorMasterScheduleSearch,
  } = useFetchMasterSchedule({
    pageSize,
    pageNumber,
    projectId,
    ...(sortByQuery || {}),
    filter: Object.keys(localFilter).length > 0 ? (localFilter as IMasterScheduleSearchFilter) : undefined,
  });

  const createUserRoleColumn = (accessor: string, header: string) => {
    return createTableUserRoleColumn<IMasterScheduleSearchResult>(accessor, header, false, false, ({ value, item }) => {
      let className = '';
      if (item.isCancelled) className = 'gcl-gems-master-schedule-list-t1-ref-no-cancelled';
      return className;
    });
  };

  // #region Master Schedule Table Columns
  const MasterScheduleTableColumns = React.useMemo(() => {
    let classNameT1 = 'gcl-gems-master-schedule-list-t1-ref-no';
    return [
      createTableLinkWithOpenNewTabColumn<IMasterSchedule>(
        'refNo',
        'T1',
        ({ item }) => {
          return generateRoutePath(routes.t1.view.path, { id: item.id.toString() });
        },
        ({ value, item, index, cellExtras }, link) => {
          let statusIcon = null;
          classNameT1 = 'gcl-gems-master-schedule-list-t1-ref-no';
          if ((item as IMasterScheduleHeader).isHeader)
            return <div className="gcl-gems-t1-list-header-cell">{value}</div>;
          if (item.isLocked)
            statusIcon = <Icon iconName="Binoculars" className="gcl-gems-master-schedule-list-t1-ref-no gcl-ml-xs" />;

          if (item.isCancelled) {
            classNameT1 = 'gcl-gems-master-schedule-list-t1-ref-no-cancelled';
            statusIcon = <Icon iconName="X" className="gcl-gems-design-brief-list-not-released-icon gcl-ml-xs" />;
          }
          return (
            <div className={classNameT1}>
              {link}
              {statusIcon}
            </div>
          );
        },
      ),

      createTableColumn({
        accessor: 'designPackage',
        header: 'Package',
        disableResizing: true,
        disableSortBy: true,
        cellRenderer: createTableCell<string, IMasterScheduleSearchResult>(
          (value: string, item: IMasterScheduleSearchResult, index: number, cellExtras: { [key: string]: any }) => {
            return <div className={classNameT1}>{item.designPackage}</div>;
          },
        ),
      }),
      createTableColumn({
        accessor: 'riskCategory',
        header: 'Risk Cat.',
        disableResizing: true,
        disableSortBy: true,
        cellRenderer: createTableCell<string, IMasterScheduleSearchResult>(
          (value: string, item: IMasterScheduleSearchResult, index: number, cellExtras: { [key: string]: any }) => {
            return <div className={classNameT1}>{item.riskCategory}</div>;
          },
        ),
      }),
      createTableColumn({
        accessor: 'voItem',
        header: 'VO Item',
        disableResizing: true,
        disableSortBy: true,
        cellRenderer: createTableCell<string, IMasterScheduleSearchResult>(
          (value: string, item: IMasterScheduleSearchResult, index: number, cellExtras: { [key: string]: any }) => {
            return <div className={classNameT1}>{item.voItem}</div>;
          },
        ),
      }),
      createTableColumn({
        disableSortBy: true,
        accessor: 'nominatedDesignerProjectCompany.name',
        header: 'Designer',
        disableResizing: true,
        cellRenderer: createTableCell<string, IMasterScheduleSearchResult>(
          (value: string, item: IMasterScheduleSearchResult, index: number, cellExtras: { [key: string]: any }) => {
            const designerName =
              item.nominatedDesignerProjectCompany?.companyTypeId === ECompanyTypeIdConst.PLACEHOLDER ? '' : value;
            return <div className={classNameT1}>{designerName}</div>;
          },
        ),
      }),
      createTableColumn({
        disableSortBy: true,
        accessor: 'nominatedCheckerProjectCompany.name',
        header: 'Checker',
        disableResizing: true,
        cellRenderer: createTableCell<string, IMasterScheduleSearchResult>(
          (value: string, item: IMasterScheduleSearchResult, index: number, cellExtras: { [key: string]: any }) => {
            const checkerName =
              item.nominatedCheckerProjectCompany?.companyTypeId === ECompanyTypeIdConst.PLACEHOLDER ? '' : value;
            return <div className={classNameT1}>{checkerName}</div>;
          },
        ),
      }),
      createUserRoleColumn('twc', 'TWC/GE'),
      createUserRoleColumn('twcm', 'TWCM'),
      createUserRoleColumn('srp', 'SRP/QP'),
      createTableDateTimeColumn('startConstructionDate', 'Start Construction'),
      createTableDateTimeColumn('procurementFabricationDate', 'Procurement/Fabrication'),
    ];
  }, [projectNoFromAppConfigs]);
  // #endregion Master Schedule Table Columns

  // #region Handle Master Schedule Search Result
  const [masterScheduleHeader, setMasterScheduleHeader] = React.useState<IMasterScheduleSearchResult[]>([]);
  const [masterScheduleMap, setMasterScheduleMap] = React.useState<{ [key: string]: IMasterScheduleSearchResult[] }>(
    {},
  );
  React.useEffect(() => {
    const tempT1HeaderArray: IMasterScheduleSearchResult[] = [];
    const tempT1Map: { [key: string]: IMasterScheduleSearchResult[] } = {};

    masterSchedulePagedResult?.items?.forEach((item: IMasterScheduleSearchResult) => {
      const { headerName } = item;

      if (isNullOrWhiteSpace(headerName)) {
        tempT1HeaderArray.push(item);
        return;
      }

      const headerKey = headerName ?? '';
      const tempMapItem = tempT1Map[headerKey] || [];

      if (tempMapItem.length <= 0)
        tempT1HeaderArray.push({
          id: 0,
          refNo: headerName,
          isHeader: true,
        } as IMasterScheduleSearchResult);

      tempT1Map[headerKey] = [...tempMapItem, item];
    });

    setMasterScheduleHeader(tempT1HeaderArray);
    setMasterScheduleMap(tempT1Map);
  }, [JSON.stringify(masterSchedulePagedResult)]);
  // #endregion Handle Master Schedule Search Result

  const tableConfig: IResponsivePaginatedTableProps = {
    columns: MasterScheduleTableColumns,
    paginationProps: {
      pageSizeOptions: DEFAULT_TABLE_PAGE_SIZE_OPTIONS,
      paginatorPosition: 'top',
      totalCount: masterSchedulePagedResult?.totalCount || 0,
      initialPageSize: initPageSize,
      onPageChange: async (pageIndex, newPageSize) => {
        setPageNumber(pageIndex + 1);
        setPageSize(newPageSize);
      },
    },
    sortingProps: {
      initialSortBy:
        sortByQuery && sortByQuery.sortByField
          ? [
              {
                id: sortByQuery.sortByField,
                desc: !!sortByQuery.isDesc,
              },
            ]
          : [],
      onSort: (sortBy) => {
        if (sortBy && sortBy.length >= 1) setSortByQuery({ sortByField: sortBy[0].id, isDesc: sortBy[0].desc });
        else setSortByQuery(undefined);
      },
    },
    allowSelectRow: false,
    items: [],
  };

  const shouldShowExpandTable = isXs
    ? false
    : !!(sortByQuery && sortByQuery.sortByField === DEFAULT_SORT_BY_FIELD_NAME);

  const tableLocal = React.useMemo(() => {
    if (shouldShowExpandTable)
      return (
        <ResponsivePaginatedTable
          {...tableConfig}
          classNames={{ body: 'gcl-gems-list-table-body' }}
          tableCellsProps={{
            dataCell: {
              getColSpanByItem: (item: object | undefined, column: Column, cellExtras: TableCellExtras | undefined) => {
                const headerItem = item as IMasterSchedule;
                if (!headerItem.isHeader) return undefined;

                if (column.id === 'expansion') return 1;

                if (column.Header === 'T1') return MasterScheduleTableColumns.length;

                return 0;
              },
            },
          }}
          items={masterScheduleHeader || []}
          allowExpandRow
          rowExpansionProps={{
            getRowExpandItems: async (rowItem: object) => {
              const headerItem = rowItem as IMasterScheduleSearchResult;
              if (!headerItem.isHeader) return [];
              const headerKey = headerItem.refNo ?? '';
              return masterScheduleMap[headerKey] || [];
            },
            checkRowCanExpand: (rowItem: object) => {
              const headerItem = rowItem as IMasterScheduleSearchResult;
              return !!headerItem.isHeader;
            },
          }}
        />
      );
    return (
      <ResponsivePaginatedTable
        {...tableConfig}
        items={masterSchedulePagedResult?.items || []}
        allowExpandRow={false}
      />
    );
  }, [shouldShowExpandTable, JSON.stringify(masterScheduleMap), JSON.stringify(masterScheduleHeader)]);

  return (
    <SearchPageBase
      data={masterSchedulePagedResult?.items}
      breadcrumbItems={[
        {
          text: `${projectNoFromAppConfigs} - Home`,
          prefixIconName: 'House',
          onClick: () => {
            navigate(routes.t1.path);
          },
        },
        {
          prefixIconName: 'Search',
          text: 'Search T1',
          active: true,
        },
      ]}
      title="T1 Master Schedule"
      isTableLoading={isLoadingMasterScheduleSearch}
      isTableRefetching={isRefetchingMasterScheduleSearch}
      buttonNavBarItem={[
        {
          text: 'New',
          navigateToPath: generateRoutePath(routes.t1.create.path),
          iconName: 'FileEarmarkPlus',
          componentType: ComponentType.Primary,
          showNoneInArchivedProject: true,
        },
        ...((hasPermissionInArray(appConfigs.configs.myPermissions, EMasterSchedulePermission.T1_BATCH_ADD)
          ? [
              {
                text: 'Batch Add T1',
                navigateToPath: generateRoutePath(routes.t1.batchAdd.path),
                iconName: 'FileEarmarkPlus',
                componentType: ComponentType.Primary,
                showNoneInArchivedProject: true,
              },
            ]
          : []) as IButtonNavBarItem[]),
        {
          text: 'Export',
          iconName: 'ArrowBarDown',
          componentType: ComponentType.Secondary,
          component: (
            <DownloadButton
              exportFunction={exportProjectMasterScheduleSearchExcelAsync}
              defaultExportProps={{
                projectId,
                sortByQuery,
                searchFilter: masterScheduleFilterObj,
              }}
            />
          ),
        },
        {
          text: 'Print',
          iconName: 'Printer',
          componentType: ComponentType.Secondary,
          component: (
            <DownloadButton
              exportFunction={exportProjectMasterScheduleSearchPdfAsync}
              defaultExportProps={{
                projectId,
                sortByQuery,
                searchFilter: masterScheduleFilterObj,
              }}
              prefixIconName="Printer"
              text="Print"
            />
          ),
        },
        {
          text: 'Print all QR code',
          iconName: 'QrCodeScan',
          component: (
            <DownloadButton
              exportFunction={exportProjectMasterScheduleQrPdfAllAsync}
              defaultExportProps={{
                projectId,
              }}
              prefixIconName="Printer"
              text="Print all QR code"
            />
          ),
        },
        {
          text: 'Manage T1 Headers',
          navigateToPath: generateRoutePath(routes.t1.manageHeaderTags.path),
          iconName: 'Tag',
          componentType: ComponentType.Secondary,
          showNoneInArchivedProject: true,
        },
      ]}
      searchFilter={
        <SearchFilter
          fieldConfigs={MasterScheduleSearchFieldConfigs}
          onFilterChanged={(filter) => {
            setLocalFilter(filter);
          }}
          defaultFilter={{
            [EMasterScheduleSearchKey.IS_CANCELLED]: false,
          }}
        />
      }
      appliedSearchFilter={localFilter}
      noContentLabel="No T1 created yet"
      shouldUpdateArrow={shouldShowExpandTable}
      onRenderTable={(isMenuHidden) => {
        return (
          <>
            <ApiErrorDisplay errors={[{ error: errorMasterScheduleSearch, genericError: 'Failed to get T1 list' }]} />
            {tableLocal}
          </>
        );
      }}
      legend={
        <ul>
          <li>
            <Icon iconName="X" className="gcl-gems-design-brief-list-not-released-icon" /> - Cancelled Form
          </li>
          <li>
            <Icon iconName="Binoculars" className="gcl-gems-master-schedule-list-locked-icon" /> - Locked
          </li>
        </ul>
      }
    />
  );
};
